import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Sparkles } from "lucide-react";
import { differenceInCalendarDays, parseISO } from "date-fns";

import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { useTenantSubscription } from "@/platform/billing/use-subscription";
import type { PlatformTenantSlug } from "@/platform/lib/database.types";

/**
 * Banner soft-gate de assinatura. NÃO bloqueia o conteúdo do produto —
 * apenas sinaliza estado da assinatura para reduzir surpresa no fim do
 * trial. Esconde-se se a assinatura está ativa (não-trial).
 */
export function SubscriptionBanner({ tenant }: { tenant: PlatformTenantSlug }) {
  const { subscription, isActive, isTrialing, isLoading } = useTenantSubscription(tenant);

  if (isLoading) return null;

  // Sem assinatura — propõe começar trial
  if (!isActive) {
    return (
      <div className="rounded-2xl border border-[--color-accent]/30 bg-[color:var(--color-accent-soft)] p-5 md:flex md:items-center md:justify-between md:gap-6">
        <div>
          <Badge variant="accentSolid" className="gap-1.5">
            <Sparkles className="size-3" aria-hidden />
            7 dias grátis
          </Badge>
          <p className="mt-3 font-display text-[length:var(--text-body-lg)] font-semibold leading-tight">
            Comece seu período de teste e desbloqueie o produto completo.
          </p>
          <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Sem cobrança nos primeiros 7 dias. Cancele a qualquer momento.
          </p>
        </div>
        <Button asChild size="lg" variant="gradient" className="mt-4 md:mt-0 shrink-0">
          <Link to={`/precos?tenant=${tenant}`}>
            Ver planos
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>
    );
  }

  // Em trial — conta dias restantes
  if (isTrialing && subscription?.trial_end) {
    const days = Math.max(0, differenceInCalendarDays(parseISO(subscription.trial_end), new Date()));
    return (
      <div className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 md:flex md:items-center md:justify-between md:gap-6">
        <div>
          <Badge variant="outline" className="gap-1.5">
            <Clock className="size-3" aria-hidden />
            Trial ativo
          </Badge>
          <p className="mt-3 font-display text-[length:var(--text-body-lg)] font-semibold leading-tight">
            {days === 0
              ? "Seu trial termina hoje."
              : days === 1
                ? "Seu trial termina amanhã."
                : `Seu trial termina em ${days} dias.`}
          </p>
          <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Continue com seu plano sem interrupção. Sem multa, sem fidelidade.
          </p>
        </div>
        <Button asChild size="lg" variant="primary" className="mt-4 md:mt-0 shrink-0">
          <Link to="/conta">
            Gerenciar plano
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>
    );
  }

  // Active — nada a mostrar.
  return null;
}
